import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuthStatus } from '../../hooks/useApi';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles = [] }) => {
  const { data: authStatus, isLoading } = useAuthStatus();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!authStatus?.authenticated || !authStatus?.user) {
    // Save the current location for redirect after login
    sessionStorage.setItem('authReturnUrl', location.pathname + location.search);
    // Not authenticated, redirect to login
    return <Navigate to="/login" replace />;
  }

  // Check if user has required roles
  if (requiredRoles.length > 0) {
    const userRoles = authStatus.user.roles || [];
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));
    
    if (!hasRequiredRole) {
      // K-06 (review): say so, instead of silently landing on the dashboard (a link pasted in an e-mail did that)
      const who = requiredRoles.includes('admin') ? 'administrators'
        : `people with the ${requiredRoles.join(' or ').replace(/_/g, ' ')} role`;
      return (
        <div role="note" data-testid="role-required"
             className="mx-auto my-mp-6 max-w-md rounded-mp-md border border-mp-border bg-mp-surface p-mp-5 text-center text-mp-base text-mp-text">
          <ShieldAlert className="mx-auto mb-mp-3 h-8 w-8 text-mp-text-muted" aria-hidden />
          <p className="font-medium">This page is for {who}.</p>
          <p className="mt-mp-2 text-mp-text-muted">Ask an administrator if you need it.</p>
          <Link to="/dashboard" className="mt-mp-4 inline-block text-mp-accent hover:underline">Back to the dashboard</Link>
        </div>
      );
    }
  }

  // User is authenticated and has required roles (if any)
  return <>{children}</>;
};

export default ProtectedRoute;